// src/components/about/ExperienceDomains.tsx
import { useRef, useState, type ComponentType, type KeyboardEvent } from 'react';
import { AnimatePresence, useReducedMotion } from 'framer-motion';
import { FaBolt, FaPlaneDeparture, FaSitemap, FaWifi } from 'react-icons/fa';

import { aboutConfig, type AboutDomain, type AboutDomainIcon } from '../../config/aboutConfig';
import DomainDiagram from './DomainDiagram';
import {
  DomainsRoot,
  DomainsContainer,
  SectionHeader,
  Eyebrow,
  EyebrowMark,
  Heading,
  Lede,
  DomainsLayout,
  DomainRail,
  RailList,
  RailItem,
  RailIndex,
  RailIcon,
  RailText,
  RailLabel,
  RailHint,
  Panel,
  PanelInner,
  PanelHeader,
  PanelHeading,
  PanelIndex,
  PanelTitle,
  PanelDiagram,
  ContextText,
  MetaGrid,
  MetaBlock,
  MetaLabel,
  MetaText,
  ConceptsBlock,
  ConceptList,
  Concept,
  StackNote,
  MobileList,
  MobileDomain,
  MobileHeader,
  MobileHeading,
  MobileIconRow,
  MobileIcon,
  MobileDiagram,
} from './ExperienceDomains.styles';
import { motionSet } from './motion';

const iconMap: Record<AboutDomainIcon, ComponentType> = {
  aviation: FaPlaneDeparture,
  iot: FaWifi,
  asset: FaSitemap,
  energy: FaBolt,
};

const headingId = 'about-domains-heading';

const ScopeBlock = ({ label, text }: { label: string; text: string }) => (
  <MetaBlock>
    <MetaLabel>{label}</MetaLabel>
    <MetaText>{text}</MetaText>
  </MetaBlock>
);

const ConceptChips = ({ concepts }: { concepts: string[] }) => (
  <ConceptsBlock>
    <MetaLabel>Concepts applied</MetaLabel>
    <ConceptList>
      {concepts.map((concept) => (
        <Concept key={concept}>{concept}</Concept>
      ))}
    </ConceptList>
  </ConceptsBlock>
);

const ExperienceDomains = () => {
  const { eyebrow, heading, lede, stackNote, items } = aboutConfig.domains;
  const reduceMotion = useReducedMotion() ?? false;

  const [activeId, setActiveId] = useState(items[0].id);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const activeIndex = items.findIndex((item) => item.id === activeId);
  const active = items[activeIndex] ?? items[0];
  const motion = motionSet(reduceMotion);

  const focusTab = (index: number) => {
    const bounded = (index + items.length) % items.length;
    setActiveId(items[bounded].id);
    tabRefs.current[bounded]?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        focusTab(index + 1);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        focusTab(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusTab(0);
        break;
      case 'End':
        event.preventDefault();
        focusTab(items.length - 1);
        break;
      default:
        break;
    }
  };

  const renderDomainBody = (domain: AboutDomain) => (
    <>
      <PanelHeader>
        <PanelHeading>
          <PanelIndex>{domain.index}</PanelIndex>
          <PanelTitle>{domain.industry}</PanelTitle>
        </PanelHeading>
        <PanelDiagram>
          <DomainDiagram variant={domain.diagram} />
        </PanelDiagram>
      </PanelHeader>

      <ContextText>{domain.context}</ContextText>

      <MetaGrid>
        <ScopeBlock label="System scope" text={domain.scope} />
        <ScopeBlock label="Engineering contribution" text={domain.contribution} />
      </MetaGrid>

      <ConceptChips concepts={domain.concepts} />
    </>
  );

  return (
    <DomainsRoot aria-labelledby={headingId}>
      <DomainsContainer>
        <SectionHeader
          variants={motion.reveal}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
        >
          <Eyebrow>
            <EyebrowMark aria-hidden="true" />
            {eyebrow}
          </Eyebrow>
          <Heading id={headingId}>{heading}</Heading>
          <Lede>{lede}</Lede>
        </SectionHeader>

        {/* Desktop / tablet: vertical selector with a single detail panel */}
        <DomainsLayout>
          <DomainRail>
            <RailList role="tablist" aria-orientation="vertical" aria-label="Engineering domains">
              {items.map((item, index) => {
                const Icon = iconMap[item.icon];
                const isActive = item.id === active.id;

                return (
                  <RailItem
                    key={item.id}
                    ref={(node) => {
                      tabRefs.current[index] = node;
                    }}
                    type="button"
                    role="tab"
                    id={`domain-tab-${item.id}`}
                    aria-selected={isActive}
                    aria-controls={`domain-panel-${item.id}`}
                    tabIndex={isActive ? 0 : -1}
                    $active={isActive}
                    onClick={() => setActiveId(item.id)}
                    onKeyDown={(event) => handleKeyDown(event, index)}
                  >
                    <RailIndex $active={isActive} aria-hidden="true">
                      {item.index}
                    </RailIndex>
                    <RailIcon $active={isActive} aria-hidden="true">
                      <Icon />
                    </RailIcon>
                    <RailText>
                      <RailLabel $active={isActive}>{item.shortLabel}</RailLabel>
                      <RailHint>{item.scope}</RailHint>
                    </RailText>
                  </RailItem>
                );
              })}
            </RailList>
          </DomainRail>

          <Panel
            role="tabpanel"
            id={`domain-panel-${active.id}`}
            aria-labelledby={`domain-tab-${active.id}`}
            tabIndex={0}
          >
            <AnimatePresence mode="wait" initial={false}>
              <PanelInner
                key={active.id}
                variants={motion.panel}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {renderDomainBody(active)}
              </PanelInner>
            </AnimatePresence>
          </Panel>
        </DomainsLayout>

        {/* Mobile: every domain stacked, driven by the same config */}
        <MobileList>
          {items.map((item) => {
            const Icon = iconMap[item.icon];

            return (
              <MobileDomain key={item.id}>
                <MobileHeader>
                  <MobileHeading>
                    <PanelIndex>{item.index}</PanelIndex>
                    <MobileIconRow>
                      <MobileIcon aria-hidden="true">
                        <Icon />
                      </MobileIcon>
                      <PanelTitle>{item.industry}</PanelTitle>
                    </MobileIconRow>
                  </MobileHeading>
                  <MobileDiagram>
                    <DomainDiagram variant={item.diagram} />
                  </MobileDiagram>
                </MobileHeader>

                <ContextText>{item.context}</ContextText>

                <MetaGrid>
                  <ScopeBlock label="System scope" text={item.scope} />
                  <ScopeBlock label="Engineering contribution" text={item.contribution} />
                </MetaGrid>

                <ConceptChips concepts={item.concepts} />
              </MobileDomain>
            );
          })}
        </MobileList>

        <StackNote>
          <strong>Applied across all four domains</strong> — {stackNote}
        </StackNote>
      </DomainsContainer>
    </DomainsRoot>
  );
};

export default ExperienceDomains;
